import {Injectable, Injector}           from '@angular/core';
import {Observable, of}                 from 'rxjs';
import {TaskManagementDataProviderBase} from './task-management-data-provider.base';
import {ITaskManagementDataProvider}    from './task-management-data-provider.interface';
import {TaskManagementConstants}        from '../task-management.constants';
import {
  IExportFormatsComboData
}                                       from '../components/wizard/wizard-ui-config/task-management-wizard-details.config';
import {ILegend}                        from '../../stores/map/map.legend.store';
import {OperationModesEnum}             from '../../stores/common/operation-modes.store';
import {N_PLANNING}                     from '../../ap-map/layers/layer.legends';
import {TranslationStore}               from '../../stores/translation/translation.store';
import {FertilizerStore}                from '../../stores/base-data/fertilizer.store';
import {FertilizationManagementService} from '../../services/data/fertilization-management.service';
import {IGridStatus}                    from '../../ap-interface/interfaces/ap-grid-status.interface';
import {ApColumnState}                  from '../../ap-interface/enums/ap-column-state.enum';
import {MapLayerTooltipFunction}        from '../../ap-map/layers/map-factory-layer';
import ITaskManagementPlanSummary = Data.TaskManagement.ITaskManagementPlanSummary;
import ITaskManagementPlanBook = Data.TaskManagement.ITaskManagementPlanBook;

@Injectable({providedIn: 'root'})
export class TaskManagementDataProviderNfert extends TaskManagementDataProviderBase implements ITaskManagementDataProvider {
  private readonly mapProviderBaseName = 'pro_n_planning';
  private readonly mapBaseName = 'map_n_planning';

  constructor(injector: Injector,
              public translationStore: TranslationStore,
              public fertilizerStore: FertilizerStore,
              public fertilizationManagementService: FertilizationManagementService) {
    super(injector);
  }

  public getComponentUrl(): string {
    return 'n-fertilization/planning';
  }

  public getStatus(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): IGridStatus {
    const baseStatus = super.getStatus(summaryOrPlanBook);
    baseStatus.description = baseStatus.state === ApColumnState.Green
      ? 'N-Grün'
      : baseStatus.state === ApColumnState.Yellow
        ? 'N-Gelb'
        : 'N-Rot';
    return baseStatus;
  }

  public getModuleText(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    if (!summaryOrPlanBook) {
      return '';
    }
    return this.translationStore.FindTranslationForSelectedLanguage('Task_Mgmt_ND');
  }

  public getProductName(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    if (!summaryOrPlanBook?.OperationMode?.Key || summaryOrPlanBook.OperationMode.Key === OperationModesEnum.Bonitur) {
      return '-';
    }
    if (summaryOrPlanBook.ProductId === 0) {
      return this.translationStore.FindTranslationForSelectedLanguage('Nutrient__PureNutrient');
    }
    const product = this.fertilizerStore.getFertilizer(summaryOrPlanBook.ProductId);
    if (!product) {
      return '-';
    }
    return product.Prodname;
  }

  public getProductGridHeader(_: ITaskManagementPlanSummary): string {
    return `${this.translationStore.FindTranslationForSelectedLanguage('Global__NutrientN')}
    ${this.translationStore.FindTranslationForSelectedLanguage('Nutrients__kg_N_per_ha')}`;
  }

  public getApplRate(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (!summaryOrPlanBook) {
      return undefined;
    }
    return summaryOrPlanBook?.ApplRate;
  }

  public getApplMin(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (!summaryOrPlanBook || this.isSummary(summaryOrPlanBook)) {
      return undefined;
    }
    return (summaryOrPlanBook as ITaskManagementPlanBook)?.ApplMin;
  }

  public getApplMax(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (!summaryOrPlanBook || this.isSummary(summaryOrPlanBook)) {
      return undefined;
    }
    return (summaryOrPlanBook as ITaskManagementPlanBook)?.ApplMax;
  }

  public getApplSum(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook | null): number | undefined {
    if (!summaryOrPlanBook) {
      return undefined;
    }
    // APV49-1517: For the N fertilization variants N fertilization and Rapeseed abs., please do not calculate the total.
    // This is only possible for N fertilization with the target value and spread map.
    if (summaryOrPlanBook.OperationMode?.Key !== OperationModesEnum.TargetValue
      && summaryOrPlanBook.OperationMode?.Key !== OperationModesEnum.ApplicationMap
      && summaryOrPlanBook.OperationMode?.Key !== OperationModesEnum.ApplicationMapAbsolute
      && summaryOrPlanBook.OperationMode?.Key !== OperationModesEnum.Constant
    ) {
      return undefined;
    }
    let applSum = 0;
    const planBooks = this.isSummary(summaryOrPlanBook)
      ? (summaryOrPlanBook as ITaskManagementPlanSummary).PlanBooks
      : [summaryOrPlanBook as ITaskManagementPlanBook];
    if (summaryOrPlanBook.OperationMode?.Key === OperationModesEnum.ApplicationMap
      || summaryOrPlanBook.OperationMode?.Key !== OperationModesEnum.ApplicationMapAbsolute) {
      for (const planBookItem of planBooks) {
        applSum += planBookItem.ApplRate * planBookItem.AreaHa;
      }
    } else {
      applSum = summaryOrPlanBook.ApplRate * summaryOrPlanBook.AreaHa;
    }
    if (summaryOrPlanBook.ProductId > 0) {
      applSum = this.nutrientService.convertNutrientToGoods(summaryOrPlanBook.ProductId, summaryOrPlanBook.Element?.Id, applSum);
    }
    return applSum;
  }

  public getProductUnit(planSummaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook,
                        perHa: boolean = false): string {
    // Special case for N:
    // The per hectare figure is always given in kg N/ha.
    // However, the totals refer to the actual product!
    if (perHa) {
      return this.translationStore.FindTranslationForSelectedLanguage('Global__Unit_kgN_ha');
    }
    // APV49-1517: For the N fertilization variants N fertilization and Rapeseed abs., please do not calculate the total.
    // This is only possible for N fertilization with a target value and a spread map.
    if (planSummaryOrPlanBook.OperationMode?.Key !== OperationModesEnum.TargetValue
      && planSummaryOrPlanBook.OperationMode?.Key !== OperationModesEnum.ApplicationMap
      && planSummaryOrPlanBook.OperationMode?.Key !== OperationModesEnum.ApplicationMapAbsolute
      && planSummaryOrPlanBook.OperationMode?.Key !== OperationModesEnum.Constant) {
      return '';
    }
    const product = this.fertilizerStore.getFertilizer(planSummaryOrPlanBook?.ProductId);
    let unit = 'Global__Unit_kg'; // fallback
    if (product && product.Unit && product.Unit.BaseUnit) {
      unit = product.Unit.BaseUnit.Key;
    } else if (product && product.Unit) {
      unit = product.Unit.Key;
    }
    return this.translationStore.FindTranslationForSelectedLanguage(unit);
  }

  public getElementName(_: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    return this.translationStore.FindTranslationForSelectedLanguage('Global__NutrientN');
  }

  public getExportFormats(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): Array<IExportFormatsComboData> {
    if (!summaryOrPlanBook || !summaryOrPlanBook.ApplicationMode || !summaryOrPlanBook.OperationMode) {
      return [];
    }
    const exportFormatFilter = new Array<string>();
    if ([
      OperationModesEnum.ApplicationMap,
      OperationModesEnum.ApplicationMapAbsolute,
      OperationModesEnum.Constant
    ].includes(summaryOrPlanBook.OperationMode.Key as OperationModesEnum)) {
      exportFormatFilter.AddRange([
        TaskManagementConstants.EXPORT_FORMAT_SHP_POLYGON,
        TaskManagementConstants.EXPORT_FORMAT_ISO,
        TaskManagementConstants.EXPORT_FORMAT_PFBOX_RST,
        TaskManagementConstants.EXPORT_FORMAT_PDF]
      );
    } else {
      exportFormatFilter.AddRange([
        TaskManagementConstants.EXPORT_FORMAT_PFBOX_RST,
        TaskManagementConstants.EXPORT_FORMAT_PDF]
      );
    }
    return super.getFilteredExportFormats(exportFormatFilter);

  }

  public getMapFactoryUrl(plans: ITaskManagementPlanBook[]): string {
    if (!plans || plans.length < 1) {
      return '';
    }
    return super.getMapFactoryUrl(plans, `${this.mapProviderBaseName}`, `${this.mapBaseName}`);
  }

  public getMapLegend(summary: ITaskManagementPlanSummary): () => ILegend {
    const nMin = summary.PlanBooks?.length > 0 ? Math.min(...summary.PlanBooks?.map(_ => _.ApplMin)) : 0;
    const nMax = summary.PlanBooks?.length > 0 ? Math.min(...summary.PlanBooks?.map(_ => _.ApplMax)) : 0;
    const nAvg = summary.PlanBooks?.length > 0 ?
      (summary.PlanBooks?.reduce((a, b) => a + b.ApplRate, 0) / summary.PlanBooks?.length) : 0;
    const productId = summary.PlanBooks?.length > 0 ? summary.PlanBooks[0].ProductId : -1;
    return () => this.convertToTaskMgmtLegend(N_PLANNING(this.translationStore.FindTranslationForSelectedLanguage.bind(this.translationStore), this.fertilizationManagementService, this.fertilizerStore, {
      Max: nMax,
      ElementId: 5,
      ProductId: productId,
      Min: nMin,
      Average: nAvg
    }));
  }

  public getMapLayerTooltip(): MapLayerTooltipFunction {
    return this.mapLayerTooltipStore.getNApplicationMapLayerTooltip();
  }

  public getExportBaseFilename(summary: ITaskManagementPlanSummary, planBook: ITaskManagementPlanBook): string {
    let cropTypeShort = '';
    if (planBook.MainCropId > 0) {
      cropTypeShort = this.translationStore.FindTranslationForSelectedLanguage(
        this.cropTypeStore.getDefaultCroptype(planBook.MainCropId)?.Shortname);
    }
    const applDate = this.dateService.dateToMoment(planBook?.ApplDate ? planBook.ApplDate : new Date()).format('YYMMDD');
    const note = (summary?.Remark ? `${this.textProcessorService.replaceSpecialChars(summary?.Remark.substring(0, 20))}` : '');
    let filename = `${applDate}${(note?.length > 0 ? '_' : '')}${note}`;
    const productName = this.getProductName(summary);
    if (!!productName && productName !== '-') {
      filename += `_${this.textProcessorService.replaceSpecialNumbersInElements(productName)}`;
    }
    if (cropTypeShort?.length > 0) {
      filename += `_${cropTypeShort}`;
    }
    return filename;
  }

  public deletePlannings(_: ITaskManagementPlanSummary | ITaskManagementPlanBook[]): void {
  }

  public changesSaveStatus(): Observable<any> {
    return of(true);
  }
}
